import { useCallback, useEffect, useState } from "react";
import { cfFetch } from "../api/apiClient";
import type { Currency } from "../types";

type Scenario = "bad" | "mid" | "good";

export type ForecastCategoryBreakdownRow = {
  categoryId: string;
  categoryName: string;
  categoryType: string;
  amount: number;
};

export type ForecastProjectionPoint = {
  month: string;
  openingBalance: number;
  inflows: number;
  outflows: number;
  net: number;
  closingBalance: number;
  isForecast: boolean;
  categoryBreakdown?: ForecastCategoryBreakdownRow[];
};

export type ForecastProjection = {
  scenario: Scenario;
  horizonMonths: number;
  generatedAt: string;
  assumptions: Record<string, unknown>;
  points: ForecastProjectionPoint[];
  displayCurrency?: Currency;
};

type UseForecastProjectionOptions = {
  horizonMonths?: number;
  lastHistoricalMonth?: string | null;
  /** When both are set, scenario state is owned by the caller (e.g. CashflowContext). */
  scenario?: Scenario;
  setScenario?: (next: Scenario) => void;
  consolidated?: boolean;
};

export function useForecastProjection(
  defaultScenario: Scenario = "mid",
  displayCurrency: Currency = "USD",
  options: UseForecastProjectionOptions = {},
) {
  const consolidated = options.consolidated ?? true;
  const horizonMonths = options.horizonMonths ?? 12;
  const lastHistoricalMonth = options.lastHistoricalMonth ?? null;
  const [internalScenario, setInternalScenario] =
    useState<Scenario>(defaultScenario);
  const controlled =
    options.scenario !== undefined && options.setScenario !== undefined;
  const scenario = controlled ? options.scenario : internalScenario;
  const setScenario = controlled ? options.setScenario : setInternalScenario;
  const [data, setData] = useState<ForecastProjection | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(
    async (nextScenario: Scenario) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({
          scenario: nextScenario,
          horizonMonths: String(horizonMonths),
          displayCurrency: String(displayCurrency),
          consolidated: String(consolidated),
        });
        if (lastHistoricalMonth) {
          params.set("lastHistoricalMonth", lastHistoricalMonth);
        }
        const res = await cfFetch(`/forecast?${params.toString()}`);
        const json = await res.json();
        if (!res.ok) {
          throw new Error(
            json?.detail?.error || "Failed to load forecast projection",
          );
        }
        setData(json as ForecastProjection);
      } catch (e) {
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [consolidated, displayCurrency, horizonMonths, lastHistoricalMonth],
  );

  useEffect(() => {
    void load(scenario);
  }, [
    scenario,
    load,
    consolidated,
    displayCurrency,
    horizonMonths,
    lastHistoricalMonth,
  ]);

  return {
    scenario,
    setScenario,
    data,
    isLoading,
    refresh: () => load(scenario),
  };
}
